import {
  Button,
  Checkbox,
  Flex,
  Metric,
  MetricGroup,
  Pill,
  ScreenReaderContent,
  SimpleSelect,
  Spinner,
  Table,
  Text,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GoalObjectiveTreeSelect from "../../../components/Selectors/GoalObjectiveTreeSelect";
import { ExternalService } from "../../../services/ExternalService";
import { Files } from "../../../services/FilesService";
import { StructuredQuestionService } from "../../../services/StructuredQuestionService";
import { TermsContext } from "../../../utils/Context";
import {
  buildGoalTreeFromMeasures,
  NO_OBJECTIVE_ID,
} from "../../../utils/buildGoalTreeFromMeasures";
import { sort } from "../../../utils/utils";
import GroupByToggle from "../GroupByToggle";
import {
  groupItemsBySection,
  groupItemsByGoalObjective,
} from "../utils/groupingUtils";

const STATUSES = {
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  SUBMITTED: "submitted",
};

const GROUPS = {
  OVERDUE: "overdue",
  NEEDS_ATTENTION: "needs_attention",
  COMPLETE: "complete",
};

const STATUS_COLORS = {
  [STATUSES.NOT_STARTED]: "primary",
  [STATUSES.IN_PROGRESS]: "warning",
  [STATUSES.SUBMITTED]: "success",
};

/**
 * Filters authoring items by goal/objective selection.
 * When both selectedGoalIds and selectedObjectiveIds are empty, returns all items.
 *
 * Filtering rules:
 * - If a goal is selected: include items with that goalId. However, if the item has
 *   an objectiveId that belongs to this goal but is NOT in selectedObjectiveIds,
 *   exclude it (the user explicitly deselected that objective).
 * - If only objectives are selected (their parent goal is not selected): include
 *   items whose objectiveId matches a selected objective.
 * - Items with no goal/objective data are excluded when any filter is active.
 *
 * @param {Array} items - Array of authoring items with goalId and objectiveId fields
 * @param {string[]} selectedGoalIds - Array of selected goal IDs
 * @param {string[]} selectedObjectiveIds - Array of selected objective IDs
 * @param {Array} goals - The goals tree: [{ id, objectives: [{ id }] }]
 * @returns {Array} Filtered items
 */
export function filterByGoalObjective(
  items,
  selectedGoalIds,
  selectedObjectiveIds,
  goals = []
) {
  if (selectedGoalIds.length === 0 && selectedObjectiveIds.length === 0) {
    return items;
  }

  // Build a lookup: goalId -> set of all its objectiveIds
  const goalObjectivesMap = new Map();
  goals.forEach((goal) => {
    goalObjectivesMap.set(
      goal.id,
      new Set((goal.objectives || []).map((o) => o.id))
    );
  });

  return items.filter((item) => {
    if (!item.goalId && !item.objectiveId) return false;

    // Check if the item's goal is selected
    if (item.goalId && selectedGoalIds.includes(item.goalId)) {
      // Goal is selected. If the item has an objective in the goal's tree,
      // only include if that objective is selected.
      // If the item has NO objective, include only if NO_OBJECTIVE_ID is selected for this goal.
      if (item.objectiveId) {
        const goalObjs = goalObjectivesMap.get(item.goalId);
        if (goalObjs && goalObjs.has(item.objectiveId)) {
          return selectedObjectiveIds.includes(item.objectiveId);
        }
      } else {
        // Item has goal but no objective — check if "No objective" is selected for this goal
        const noObjKey = `${item.goalId}:${NO_OBJECTIVE_ID}`;
        return selectedObjectiveIds.includes(noObjKey);
      }
      return true;
    }

    // Goal is not selected — check if the item's objective is selected
    if (item.objectiveId && selectedObjectiveIds.includes(item.objectiveId)) {
      return true;
    }

    // Check if "No objective" for this goal is selected (goal not selected but no-obj is)
    if (item.goalId && !item.objectiveId) {
      const noObjKey = `${item.goalId}:${NO_OBJECTIVE_ID}`;
      return selectedObjectiveIds.includes(noObjKey);
    }

    return false;
  });
}

const AuthoringAndReview = ({
  plan,
  isExternal,
  onAddToAgenda,
  agenda,
  groupBy,
  setGroupBy,
}) => {
  const { t } = useTranslation();
  const { insightsTerms: contextInsightsTerms } = useContext(TermsContext);

  // For external access, fetch insights terms if context is empty
  const [externalInsightsTerms, setExternalInsightsTerms] = useState([]);
  useEffect(() => {
    if ((!contextInsightsTerms || contextInsightsTerms.length === 0) && plan) {
      const fetchTerms = async () => {
        try {
          const terms = await ExternalService.getInsightsTerms();
          setExternalInsightsTerms(terms || []);
        } catch {
          setExternalInsightsTerms([]);
        }
      };
      fetchTerms();
    }
  }, [contextInsightsTerms, plan]);

  const insightsTerms =
    contextInsightsTerms && contextInsightsTerms.length > 0
      ? contextInsightsTerms
      : externalInsightsTerms;

  const [selectedSection, setSelectedSection] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("");
  const [selectedGoalIds, setSelectedGoalIds] = useState([]);
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Compute effective grouping: force "section" when plan doesn't support goals/objectives
  const effectiveGroupBy = plan?.include_goals_objs ? groupBy : "section";

  // Handle group-by toggle changes with selection clearing
  const handleGroupByChange = (newValue) => {
    if (newValue === "section" && groupBy === "goalObjective") {
      setSelectedGoalIds([]);
      setSelectedObjectiveIds([]);
    }
    setGroupBy(newValue);
  };
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [measureStatuses, setMeasureStatuses] = useState({});
  const [statusesLoading, setStatusesLoading] = useState(true);

  // Set of measure IDs already in the agenda — pre-select and disable their checkboxes
  const agendaSourceIds = useMemo(() => {
    if (!agenda?.items) return new Set();
    return new Set(
      agenda.items
        .filter((i) => i.source_type === "qualitative_measure")
        .map((i) => i.source_id)
    );
  }, [agenda]);
  // Reset goal/objective selections when plan changes
  useEffect(() => {
    setSelectedGoalIds([]);
    setSelectedObjectiveIds([]);
  }, [plan]);

  // Fetch S3-based statuses for each qualitative measure
  useEffect(() => {
    if (!plan?.sections) {
      setStatusesLoading(false);
      return;
    }

    // Extract all unique measure ddl_ids from the plan
    const measureIds = [];
    plan.sections.forEach((section) => {
      if (section.qualitative_measures) {
        section.qualitative_measures.forEach((measure) => {
          if (measure.ddl_id && !measureIds.includes(measure.ddl_id)) {
            measureIds.push(measure.ddl_id);
          }
        });
      }
    });

    if (measureIds.length === 0) {
      setMeasureStatuses({});
      setStatusesLoading(false);
      return;
    }

    setStatusesLoading(true);
    setMeasureStatuses({});

    let cancelled = false;

    const getStructuredQuestions = isExternal
      ? ExternalService.getStructuredQuestions
      : StructuredQuestionService.getStructuredQuestions;

    const reviewFiles = isExternal
      ? ExternalService.reviewFiles.bind(ExternalService)
      : Files.reviewFiles.bind(Files);

    const fetchStatuses = async () => {
      const statusMap = {};

      for (const measureId of measureIds) {
        if (cancelled) return;
        try {
          // Check if this measure has structured questions
          let questions = [];
          try {
            questions = await getStructuredQuestions(measureId);
            if (!Array.isArray(questions)) questions = [];
          } catch {
            questions = [];
          }

          if (questions.length > 0) {
            // Structured questions: check each question path
            const questionResults = await Promise.allSettled(
              questions.map((q) =>
                reviewFiles(
                  "qualitativeEvidence",
                  `${measureId}/${q.ddl_id || q.id}`
                )
              )
            );

            let anyPublished = false;
            let anyDraft = false;
            questionResults.forEach((result) => {
              if (result.status === "fulfilled") {
                const files = result.value || [];
                if (
                  files.some(
                    (f) =>
                      f.file_name === "response.html" &&
                      !f.file_url.includes("/draft/")
                  )
                ) {
                  anyPublished = true;
                }
                if (files.some((f) => f.file_url.includes("/draft/"))) {
                  anyDraft = true;
                }
              }
            });

            if (anyPublished) {
              statusMap[measureId] = "submitted";
            } else if (anyDraft) {
              statusMap[measureId] = "in_progress";
            } else {
              statusMap[measureId] = "not_started";
            }
          } else {
            // No structured questions: check measure-level path
            const files = await reviewFiles("qualitativeEvidence", measureId);
            const hasPublished = (files || []).some(
              (f) =>
                f.file_name === "response.html" &&
                !f.file_url.includes("/draft/")
            );
            const hasDraft = (files || []).some((f) =>
              f.file_url.includes("/draft/")
            );

            if (hasPublished) {
              statusMap[measureId] = "submitted";
            } else if (hasDraft) {
              statusMap[measureId] = "in_progress";
            } else {
              statusMap[measureId] = "not_started";
            }
          }
        } catch {
          statusMap[measureId] = "not_started";
        }
      }

      if (!cancelled) {
        setMeasureStatuses(statusMap);
        setStatusesLoading(false);
      }
    };

    fetchStatuses();

    return () => {
      cancelled = true;
    };
  }, [plan]);

  // Aggregate all qualitative measures into a flat array for tree building
  const measures = useMemo(() => {
    if (!plan?.sections) return [];
    const allMeasures = [];
    plan.sections.forEach((section) => {
      if (section.qualitative_measures) {
        section.qualitative_measures.forEach((measure) => {
          allMeasures.push(measure);
        });
      }
    });
    return allMeasures;
  }, [plan]);

  // Build goals tree from measures
  const goals = useMemo(() => buildGoalTreeFromMeasures(measures), [measures]);

  // Build flat list of items from qualitative measures only
  const authoringItems = useMemo(() => {
    if (!plan?.sections) return [];
    const items = [];

    plan.sections.forEach((section) => {
      const sectionName = section.name || "";

      if (section.qualitative_measures) {
        section.qualitative_measures.forEach((measure) => {
          const measureName = measure.name || "";
          const assignees = measure.assignees
            ? measure.assignees
                .split(",")
                .map((e) => e.trim())
                .filter(Boolean)
            : [];

          // Resolve due term name from insights_term_id
          let dueTerm = "";
          if (measure.insights_term_id && insightsTerms) {
            const term = insightsTerms.find(
              (it) => it.id === measure.insights_term_id
            );
            if (term) dueTerm = term.name;
          }

          // Status comes from the S3-fetched measureStatuses map
          const status =
            measureStatuses[measure.ddl_id] || STATUSES.NOT_STARTED;

          // Determine group based on status + due term logic
          let group;
          if (status === STATUSES.SUBMITTED) {
            group = GROUPS.COMPLETE;
          } else if (measure.insights_term_id && insightsTerms) {
            const term = insightsTerms.find(
              (it) => it.id === measure.insights_term_id
            );
            if (term && term.end_at && new Date(term.end_at) < new Date()) {
              group = GROUPS.OVERDUE;
            } else {
              group = GROUPS.NEEDS_ATTENTION;
            }
          } else {
            group = GROUPS.NEEDS_ATTENTION;
          }

          items.push({
            id: `${plan.id}-${section.id || sectionName}-measure-${
              measure.ddl_id
            }`,
            measureId: measure.ddl_id,
            persons: assignees,
            sectionName,
            measureName,
            due: dueTerm,
            insightsTermId: measure.insights_term_id || "",
            goalDescription: measure.goal?.description || "",
            goalId: measure.goal?.id || null,
            objectiveId: measure.objective?.id || null,
            objectiveDescription: measure.objective?.description || "",
            status,
            group,
          });
        });
      }
    });

    return items;
  }, [plan, insightsTerms, measureStatuses]);

  // Available section names for filter
  const sectionOptions = useMemo(() => {
    if (!plan?.sections) return [];
    return sort(
      plan.sections
        .filter(
          (s) => s.qualitative_measures && s.qualitative_measures.length > 0
        )
        .map((s) => s.name)
        .filter(Boolean)
        .filter((v, i, a) => a.indexOf(v) === i)
    );
  }, [plan]);

  // Available terms for filter (from qualitative measures' insights_term_id)
  const termOptions = useMemo(() => {
    if (!plan?.sections || !insightsTerms) return [];
    const termIds = new Set();
    plan.sections.forEach((section) => {
      if (section.qualitative_measures) {
        section.qualitative_measures.forEach((measure) => {
          if (measure.insights_term_id) termIds.add(measure.insights_term_id);
        });
      }
    });
    return sort(
      [...termIds]
        .map((id) => {
          const term = insightsTerms.find((t) => t.id === id);
          return term ? { id: term.id, label: term.name } : null;
        })
        .filter(Boolean),
      (item) => item.label
    );
  }, [plan, insightsTerms]);

  // Handle goal/objective tree selection changes
  const handleGoalObjectiveChange = ({
    goalIds = [],
    objectiveIds = [],
  } = {}) => {
    setSelectedGoalIds(goalIds);
    setSelectedObjectiveIds(objectiveIds);
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    // Apply goal/objective filter
    const items = filterByGoalObjective(
      authoringItems,
      selectedGoalIds,
      selectedObjectiveIds,
      goals
    );

    // Apply remaining filters with AND logic
    return items.filter((item) => {
      if (selectedSection && item.sectionName !== selectedSection) return false;
      if (selectedTerm && item.insightsTermId !== selectedTerm) return false;
      if (selectedStatus && item.status !== selectedStatus) return false;
      return true;
    });
  }, [
    authoringItems,
    selectedSection,
    selectedTerm,
    selectedGoalIds,
    selectedObjectiveIds,
    selectedStatus,
    goals,
  ]);

  // Summary counts for MetricGroup (kept for stats display)
  const overdueCount = useMemo(
    () => filteredItems.filter((item) => item.group === GROUPS.OVERDUE).length,
    [filteredItems]
  );
  const needsAttentionCount = useMemo(
    () =>
      filteredItems.filter((item) => item.group === GROUPS.NEEDS_ATTENTION)
        .length,
    [filteredItems]
  );
  const completeCount = useMemo(
    () => filteredItems.filter((item) => item.group === GROUPS.COMPLETE).length,
    [filteredItems]
  );

  // Section-based grouping
  const sectionGroups = useMemo(
    () => groupItemsBySection(filteredItems),
    [filteredItems]
  );

  // Goal/Objective-based grouping
  const goalObjectiveGroups = useMemo(
    () => groupItemsByGoalObjective(filteredItems, goals),
    [filteredItems, goals]
  );

  // Compute displayed selected count: user-selected + agenda items visible in filtered view
  const displayedSelectedCount = useMemo(() => {
    const agendaCheckedInFiltered = filteredItems.filter((item) =>
      agendaSourceIds.has(item.measureId)
    ).length;
    return selectedItems.length + agendaCheckedInFiltered;
  }, [filteredItems, agendaSourceIds, selectedItems]);

  // Determine if any filtered item is already in the agenda (disable all selection UI)
  const anyFilteredInAgenda = useMemo(() => {
    return filteredItems.some((item) => agendaSourceIds.has(item.measureId));
  }, [filteredItems, agendaSourceIds]);

  // Uncheck selectAll when filtered items change (filter applied/removed)
  const prevFilteredCountRef = useRef(filteredItems.length);
  useEffect(() => {
    if (filteredItems.length !== prevFilteredCountRef.current) {
      setSelectAll(false);
    }
    prevFilteredCountRef.current = filteredItems.length;
  }, [filteredItems]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
      setSelectAll(false);
    } else {
      const selectableItems = filteredItems.filter(
        (item) => !agendaSourceIds.has(item.measureId)
      );
      setSelectedItems(selectableItems.map((item) => item.id));
      setSelectAll(true);
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems((prev) => {
      if (prev.includes(itemId)) {
        setSelectAll(false);
        return prev.filter((id) => id !== itemId);
      } else {
        const newSelection = [...prev, itemId];
        const selectableCount = filteredItems.filter(
          (item) => !agendaSourceIds.has(item.measureId)
        ).length;
        if (newSelection.length === selectableCount) {
          setSelectAll(true);
        }
        return newSelection;
      }
    });
  };

  const handleAddToAgenda = () => {
    if (!onAddToAgenda || selectedItems.length === 0) return;

    // Get the selected item objects and deduplicate by measureId
    const selectedItemObjects = filteredItems.filter((item) =>
      selectedItems.includes(item.id)
    );

    // Deduplicate by measureId (multiple assignees for same measure should only add once)
    const seen = new Set();
    const uniqueItems = selectedItemObjects.filter((item) => {
      if (seen.has(item.measureId)) return false;
      seen.add(item.measureId);
      return true;
    });

    const mappedItems = uniqueItems.map((item) => ({
      sourceId: item.measureId,
      sourceType: "qualitative_measure",
      sectionName: item.sectionName,
      itemName: item.measureName,
      goalId: item.goalId || null,
      goalDescription: item.goalDescription || "",
      objectiveId: item.objectiveId || null,
      objectiveDescription: item.objectiveDescription || "",
    }));

    onAddToAgenda(mappedItems);
    setSelectedItems([]);
    setSelectAll(false);
  };

  const statusFilterOptions = [
    {
      id: STATUSES.NOT_STARTED,
      label: t("assessmentPlanningDashboard.authoringAndReview.notStarted"),
    },
    {
      id: STATUSES.IN_PROGRESS,
      label: t("assessmentPlanningDashboard.authoringAndReview.inProgress"),
    },
    {
      id: STATUSES.SUBMITTED,
      label: t("assessmentPlanningDashboard.authoringAndReview.submitted"),
    },
  ];

  const getStatusLabel = (status) => {
    switch (status) {
      case STATUSES.SUBMITTED:
        return t("assessmentPlanningDashboard.authoringAndReview.submitted");
      case STATUSES.IN_PROGRESS:
        return t("assessmentPlanningDashboard.authoringAndReview.inProgress");
      case STATUSES.NOT_STARTED:
      default:
        return t("assessmentPlanningDashboard.authoringAndReview.notStarted");
    }
  };

  const renderStatusPill = (item) => {
    const label = getStatusLabel(item.status);
    const color =
      item.group === GROUPS.OVERDUE
        ? "danger"
        : STATUS_COLORS[item.status] || "primary";
    return <Pill color={color}>{label}</Pill>;
  };

  const renderFlatTable = (tableItems, tableKey) => {
    if (tableItems.length === 0) return null;

    return (
      <Table caption={tableKey} hover layout="fixed">
        <Table.Head>
          <Table.Row>
            <Table.ColHeader
              id={`${encodeURIComponent(tableKey)}-select`}
              width="5%"
            >
              <ScreenReaderContent>
                {t("assessmentPlanningDashboard.dataCollection.selectMeasure")}
              </ScreenReaderContent>
            </Table.ColHeader>
            <Table.ColHeader
              id={`${encodeURIComponent(tableKey)}-measure`}
              width="40%"
            >
              {t("assessmentPlanningDashboard.columnMeasure")}
            </Table.ColHeader>
            <Table.ColHeader
              id={`${encodeURIComponent(tableKey)}-person`}
              width="35%"
            >
              {t("assessmentPlanningDashboard.authoringAndReview.columnPerson")}
            </Table.ColHeader>
            <Table.ColHeader
              id={`${encodeURIComponent(tableKey)}-due`}
              width="10%"
            >
              {t("assessmentPlanningDashboard.authoringAndReview.columnDue")}
            </Table.ColHeader>
            <Table.ColHeader
              id={`${encodeURIComponent(tableKey)}-status`}
              width="10%"
            >
              {t("assessmentPlanningDashboard.authoringAndReview.columnStatus")}
            </Table.ColHeader>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {tableItems.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell>
                <Checkbox
                  label={
                    <ScreenReaderContent>
                      {t(
                        "assessmentPlanningDashboard.authoringAndReview.selectItem",
                        { name: item.measureName }
                      )}
                    </ScreenReaderContent>
                  }
                  size="small"
                  checked={
                    selectedItems.includes(item.id) ||
                    agendaSourceIds.has(item.measureId)
                  }
                  onChange={() => handleSelectItem(item.id)}
                  disabled={anyFilteredInAgenda}
                />
              </Table.Cell>
              <Table.Cell>
                <Text weight="bold" size="small">
                  {item.measureName}
                </Text>
                <br />
                <Text size="x-small" color="secondary">
                  {item.sectionName}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text weight="bold" size="small" wrap="break-word">
                  {item.persons.length > 0 ? item.persons.join(", ") : "—"}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text size="small" color="brand">
                  {item.due || "—"}
                </Text>
              </Table.Cell>
              <Table.Cell>{renderStatusPill(item)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    );
  };

  const renderSectionGroupedTables = () => {
    return sectionGroups.map((group) => (
      <View as="div" margin="medium 0" key={group.sectionName}>
        <Text weight="bold" size="small" transform="uppercase">
          {group.sectionName} — {group.items.length}
        </Text>
        {renderFlatTable(group.items, `section-${group.sectionName}`)}
      </View>
    ));
  };

  const renderGoalObjectiveGroupedTables = () => {
    return goalObjectiveGroups.map((goalGroup) => (
      <View as="div" margin="medium 0" key={goalGroup.goalId}>
        <Text weight="bold">{goalGroup.goalDescription}</Text>
        {goalGroup.objectiveGroups.map((objGroup) => (
          <View
            as="div"
            margin="small 0 small medium"
            key={objGroup.objectiveId}
          >
            <Text weight="bold" size="small" color="secondary">
              {objGroup.objectiveDescription} — {objGroup.items.length}
            </Text>
            {renderFlatTable(objGroup.items, `obj-${objGroup.objectiveId}`)}
          </View>
        ))}
      </View>
    ));
  };

  return (
    <View as="div" padding="small 0">
      {/* Filters: GroupByToggle, Section/GoalObjective (mutually exclusive), Term, Status */}
      <Flex wrap="wrap" gap="small" margin="0 0 medium 0" alignItems="start">
        {plan?.include_goals_objs && (
          <Flex.Item>
            <GroupByToggle
              value={effectiveGroupBy}
              onChange={handleGroupByChange}
            />
          </Flex.Item>
        )}

        {effectiveGroupBy === "section" && (
          <Flex.Item>
            <SimpleSelect
              renderLabel={t("assessmentPlanningDashboard.sectionFilter")}
              value={selectedSection}
              onChange={(_e, { value }) => setSelectedSection(value)}
              width="180px"
            >
              <SimpleSelect.Option id="section-all" value="">
                {t("assessmentPlanningDashboard.allSections")}
              </SimpleSelect.Option>
              {sectionOptions.map((section) => (
                <SimpleSelect.Option
                  key={section}
                  id={`section-${section}`}
                  value={section}
                >
                  {section}
                </SimpleSelect.Option>
              ))}
            </SimpleSelect>
          </Flex.Item>
        )}

        {effectiveGroupBy === "goalObjective" && (
          <Flex.Item>
            <div style={{ minWidth: "280px" }}>
              <GoalObjectiveTreeSelect
                goals={goals}
                onChange={handleGoalObjectiveChange}
                interaction={goals.length > 0 ? "enabled" : "disabled"}
                disabledTooltip={t(
                  "assessmentPlanningDashboard.authoringAndReview.goalObjectiveDisabledTooltip"
                )}
              />
            </div>
          </Flex.Item>
        )}

        <Flex.Item>
          <SimpleSelect
            renderLabel={t(
              "assessmentPlanningDashboard.authoringAndReview.termFilter"
            )}
            value={selectedTerm}
            onChange={(_e, { value }) => setSelectedTerm(value)}
            width="180px"
          >
            <SimpleSelect.Option id="term-all" value="">
              {t("assessmentPlanningDashboard.authoringAndReview.allTerms")}
            </SimpleSelect.Option>
            {termOptions.map((term) => (
              <SimpleSelect.Option
                key={term.id}
                id={`term-${term.id}`}
                value={term.id}
              >
                {term.label}
              </SimpleSelect.Option>
            ))}
          </SimpleSelect>
        </Flex.Item>

        <Flex.Item>
          <SimpleSelect
            renderLabel={t(
              "assessmentPlanningDashboard.authoringAndReview.statusFilter"
            )}
            value={selectedStatus}
            onChange={(_e, { value }) => setSelectedStatus(value)}
            width="180px"
          >
            <SimpleSelect.Option id="status-all" value="">
              {t("assessmentPlanningDashboard.authoringAndReview.allStatuses")}
            </SimpleSelect.Option>
            {statusFilterOptions.map((option) => (
              <SimpleSelect.Option
                key={option.id}
                id={`status-${option.id}`}
                value={option.id}
              >
                {option.label}
              </SimpleSelect.Option>
            ))}
          </SimpleSelect>
        </Flex.Item>
      </Flex>

      {/* Bulk action bar */}
      <Flex
        alignItems="center"
        gap="small"
        wrap="wrap"
        margin="0 0 medium 0"
        padding="small"
      >
        <Flex.Item>
          <Checkbox
            label={t("assessmentPlanningDashboard.selectAllVisible")}
            checked={selectAll}
            onChange={handleSelectAll}
            disabled={anyFilteredInAgenda}
          />
        </Flex.Item>
        <Flex.Item>
          <Text size="small" weight="bold" color="brand">
            {t("assessmentPlanningDashboard.selectedCount", {
              count: displayedSelectedCount,
            })}
          </Text>
        </Flex.Item>
        <Flex.Item shouldGrow textAlign="end">
          <Button
            size="small"
            onClick={handleAddToAgenda}
            interaction={
              anyFilteredInAgenda
                ? "disabled"
                : selectedItems.length > 0
                  ? "enabled"
                  : "disabled"
            }
          >
            {t("assessmentPlanningDashboard.addToAgenda")}
          </Button>
        </Flex.Item>
      </Flex>

      {/* Loading state or content */}
      {statusesLoading ? (
        <View as="div" padding="large" textAlign="center">
          <Spinner
            renderTitle={t(
              "assessmentPlanningDashboard.authoringAndReview.loadingStatuses"
            )}
            size="medium"
          />
        </View>
      ) : (
        <>
          {/* Summary stat cards */}
          <View
            as="div"
            margin="0 0 medium 0"
            borderWidth="small"
            borderRadius="medium"
            padding="medium"
            background="secondary"
          >
            <MetricGroup>
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.authoringAndReview.overdueLabel"
                )}
                renderValue={overdueCount}
              />
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.authoringAndReview.notStartedOrInProgressLabel"
                )}
                renderValue={needsAttentionCount}
              />
              <Metric
                renderLabel={t(
                  "assessmentPlanningDashboard.authoringAndReview.submittedOrApprovedLabel"
                )}
                renderValue={completeCount}
              />
            </MetricGroup>
          </View>

          {/* Grouped Tables */}
          {effectiveGroupBy === "section" && renderSectionGroupedTables()}
          {effectiveGroupBy === "goalObjective" &&
            renderGoalObjectiveGroupedTables()}

          {filteredItems.length === 0 && (
            <View as="div" padding="medium" textAlign="center">
              <Text>
                {t("assessmentPlanningDashboard.authoringAndReview.noItems")}
              </Text>
            </View>
          )}
        </>
      )}
    </View>
  );
};

export default AuthoringAndReview;
